import { normalizeDate, numericToDate } from '@kizi/comparator-core';
import { isOlderThan } from '@kizi/comparator-core';
import { Button, Div, Heading, P } from '@kizi/core-components';
import { Checkbox, CheckboxProps, capitalise } from '@kizi/shared-components';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  isEmailValid,
  isEmpty,
  isMobile,
  useHandleSubmitClick,
} from '../../utils/global';
import { withFormProps } from '../HOCs';
import { BasicInput, DateInput } from '../wrappers/inputs/withFormProps';
import {
  getRendererFromDescriptor,
  useField,
  useWizardService,
} from '@kizi/comparator-core';
import { AddressWithContext } from '../wrappers/Address/AddressWithContext';

const FormCheckbox = withFormProps<CheckboxProps>(Checkbox, {
  _containerProps: {
    variant: 'formGroupInlineReversed',
    my: 3,
    alignContent: 'center',
  },
});

interface DataContextProps {
  formData: {
    firstName: string;
    prefixes?: string;
    surname: string;
    birthDate: string;
    email: string;
    postcode: string;
    houseNumber: string;
    addition: string;
    gender: string;
  };
  formErrors: {
    firstName: undefined | string[];
    prefixes: undefined;
    surname: undefined | string[];
    birthDate: undefined | string[];
    email: undefined | string[];
    postcode: undefined | string[];
    houseNumber: undefined | string[];
    addition: undefined | string[];
    gender: undefined | string[];
  };
  updateFormData: (field: string, value: any, errorMessages?: string[]) => void;
}
interface InnerModalProps {
  supplierName: string;
  handleSubmitAttributes: {
    shouldOpenNewTab: any;
    data: any;
    nextIndex: any;
    registerAnalyticsAddToCartEvent: () => void;
  };
  onClose: () => void;
  onSubmitFn?: (service: any, data: any) => void;
}

const DataContext = createContext<DataContextProps>({
  formData: {
    firstName: '',
    prefixes: '',
    surname: '',
    birthDate: '',
    email: '',
    postcode: '',
    houseNumber: '',
    addition: '',
    gender: '',
  },
  formErrors: {
    firstName: undefined,
    prefixes: undefined,
    surname: undefined,
    birthDate: undefined,
    email: undefined,
    postcode: undefined,
    houseNumber: undefined,
    addition: undefined,
    gender: undefined,
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateFormData: (
    _field: string,
    _value: any,
    _errorMessages?: string[],
    // eslint-disable-next-line @typescript-eslint/no-empty-function
  ) => {},
});

export const AffiliateDataProvider = ({ children }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    prefixes: '',
    surname: '',
    birthDate: '',
    email: '',
    postcode: '',
    houseNumber: '',
    addition: '',
    gender: '',
  });

  const [formErrors, setFormErrors] = useState({
    firstName: undefined,
    prefixes: undefined,
    surname: undefined,
    birthDate: undefined,
    email: undefined,
    postcode: undefined,
    houseNumber: undefined,
    addition: undefined,
    gender: undefined,
  });

  const updateFormData = (
    field: string,
    value: any,
    errorMessages?: string[],
  ) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: errorMessages,
    }));
  };

  return (
    <DataContext.Provider value={{ formData, formErrors, updateFormData }}>
      {children}
    </DataContext.Provider>
  );
};

export const InnerModalForAffiliates: React.FC<InnerModalProps> = React.memo(
  ({ supplierName, handleSubmitAttributes, onClose, onSubmitFn }) => {
    const [checked, setChecked] = useState(false);
    const { formData, formErrors } = useContext(DataContext);
    const {
      firstName,
      prefixes,
      surname,
      birthDate,
      email,
      gender,
      postcode,
      houseNumber,
      addition,
    } = formData;
    const service = useWizardService();
    const fullAddressField = useField('*.fullAddress');

    const parts = birthDate && birthDate.split('-');
    const convertedDate = parts && `${parts[2]}-${parts[1]}-${parts[0]}`;

    const {
      shouldOpenNewTab,
      data,
      nextIndex,
      registerAnalyticsAddToCartEvent,
    } = handleSubmitAttributes;

    const handleLinkClick = useHandleSubmitClick(
      shouldOpenNewTab,
      {
        ...data,
        affiliateIncentiveUserData: {
          userDesiresIncentive: false,
        },
      },
      nextIndex,
      registerAnalyticsAddToCartEvent,
    );

    let handleClick = useHandleSubmitClick(
      shouldOpenNewTab,
      {
        ...data,
        affiliateIncentiveUserData: checked
          ? {
              userDesiresIncentive: checked,
              callName: firstName,
              prefixes,
              surname,
              birthDate: convertedDate,
              email: email,
              postalCode: postcode,
              houseNumber,
              houseNumberAddition: addition,
              gender,
            }
          : {
              userDesiresIncentive: checked,
            },
      },
      nextIndex,
      registerAnalyticsAddToCartEvent,
    );

    const shouldDisableSubmitButton = useCallback((): boolean => {
      if (checked) {
        if (
          formErrors.surname === undefined &&
          formErrors.birthDate === undefined &&
          formErrors.email === undefined &&
          firstName.length > 0 &&
          surname.length > 0 &&
          birthDate.length > 0 &&
          formErrors.firstName === undefined &&
          email.length > 0 &&
          isEmailValid(email) &&
          gender !== undefined &&
          fullAddressField?.value !== '' &&
          fullAddressField?.value !== undefined
        ) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    }, [
      checked,
      formErrors.firstName,
      formErrors.surname,
      formErrors.birthDate,
      formErrors.email,
      firstName,
      surname,
      birthDate,
      email,
      fullAddressField?.value,
      gender,
    ]);

    const onClick = useCallback(() => {
      if (!shouldDisableSubmitButton()) {
        if (onSubmitFn) {
          onSubmitFn(service, {
            ...data,
            affiliateIncentiveUserData: checked
              ? {
                  userDesiresIncentive: checked,
                  callName: firstName,
                  prefixes,
                  surname,
                  birthDate: convertedDate,
                  email: email,
                  postalCode: postcode,
                  houseNumber,
                  houseNumberAddition: addition,
                  gender,
                }
              : {
                  userDesiresIncentive: checked,
                },
          });
        } else {
          handleClick();
        }
        onClose();
      }
    }, [shouldDisableSubmitButton, handleClick, onClose]);

    return (
      <Div>
        {!isMobile() && (
          <Heading
            variant="H4"
            as="h4"
            color="heading"
            mt={0}
            textAlign="center"
          >
            {`Rond je aanvraag af op de website ${supplierName}`}
          </Heading>
        )}
        <P variant="bodyRegular">
          {`Fijn dat je een keuze hebt kunnen maken voor een nieuwe zorgverzekering. Je staat op het punt om door te gaan naar de website van ${supplierName}. Wanneer je je gegevens achter laat krijg je van ons de Gratis Tandongevallenverzekering en toegang tot een klantomgeving van Overstappen.nl.`}
        </P>
        <FormCheckbox
          variant="main"
          _label="Ja, ik wil graag een Gratis Tandongevallenverzekering"
          _labelProps={{ variant: 'sideLabel' }}
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
        />
        {checked && (
          <Div
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            gridGap={3}
          >
            <P
              variant="captionRegular"
              style={{
                textDecoration: 'none',
                cursor: 'pointer',
                fontSize: '16px',
              }}
              color={'main.1'}
              onClick={handleLinkClick}
            >
              Klik hier om verder te gaan zonder de onderstaande gegevens in te
              vullen, je ziet dan af van de Gratis Tandongevallenverzekering.
            </P>
            <AddressNew />
            <GenderNew />
            <FirstNameNew />
            <PrefixesNew />
            <SurnameNew />
            <BirtDateNew />
            <EmailNew />
          </Div>
        )}
        <Button
          onClick={onClick}
          variant="primary"
          mt={4}
          mb={4}
          width="100% !important"
          disabled={shouldDisableSubmitButton()}
        >
          Verder met aanvragen
        </Button>
      </Div>
    );
  },
);

const AddressNew = () => {
  const { formErrors, updateFormData } = useContext(DataContext);
  const postCodeField = useField('*.postcodeAffiliates');
  const houseNumberField = useField('*.housenumber');
  const additionField = useField('*.housenumberaddition');
  const fullAddressField = useField('*.fullAddress');

  const handleInputChange = () => {
    updateFormData('postcode', postCodeField?.value, formErrors['postcode']);
    updateFormData(
      'houseNumber',
      houseNumberField?.value,
      formErrors['houseNumber'],
    );
    updateFormData('addition', additionField?.value, formErrors['addition']);
  };

  useEffect(() => {
    if (
      fullAddressField?.value !== '' &&
      fullAddressField?.value !== undefined
    ) {
      handleInputChange();
    }
  }, [postCodeField, houseNumberField, additionField, fullAddressField]);

  return (
    <AddressWithContext
      autoForward={true}
      inputProps={{
        postcode: {
          _label: 'Postcode',
          _tooltip: '1234AB',
          placeholder: '1234AB',
        },
        houseNumber: {
          _label: 'Huisnr.',
          placeholder: '123',
          type: 'tel',
        },
        addition: {
          _label: 'Toev.',
          placeholder: 'A',
        },
      }}
      fieldNames={{
        addition: '*.housenumberaddition',
        houseNumber: '*.housenumber',
        postcode: '*.postcodeAffiliates',
        city: '*.city',
        street: '*.street',
        fullAddress: '*.fullAddress',
      }}
    />
  );
};

const GenderNew = () => {
  const { formErrors, updateFormData } = useContext(DataContext);
  const genderField = useField('*.genderAffiates');
  const handleInputChange = (value) => {
    updateFormData('gender', value, formErrors['gender']);
  };

  useEffect(() => {
    handleInputChange(genderField?.value);
  }, [genderField?.value]);

  return (
    <>
      {getRendererFromDescriptor({
        type: 'TileRadioGroup',
        props: {
          _label: 'Geslacht',
          name: 'gender',
          _fieldName: '*.genderAffiates',
          direction: 'horizontal',
          style: {
            height: '54px',
            borderRadius: '8px',
          },
          radios: [
            {
              text: 'Man',
              value: 'male',
            },
            {
              text: 'Vrouw',
              value: 'female',
            },
          ],
        },
      })}
    </>
  );
};

const FirstNameNew = () => {
  const { formData, formErrors, updateFormData } = useContext(DataContext);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateFormData(name, capitalise(value).trim(), formErrors[name]);
  };

  const [errorMessage, setErrorMessage] = useState<string[] | undefined>(
    formErrors.firstName,
  );

  const validateField = (value) => {
    if (isEmpty(value)) {
      setErrorMessage(['Vul hier je voornaam in']);
    } else {
      setErrorMessage(undefined);
    }
    updateFormData('firstName', formData.firstName, errorMessage);
  };

  return (
    <BasicInput
      _label={'Voornaam'}
      _error={errorMessage}
      id="firstName"
      name="firstName"
      onClick={() => setErrorMessage(undefined)}
      onChange={handleInputChange}
      onBlur={() => validateField(formData.firstName.trim())}
      value={formData.firstName.trim()}
    />
  );
};

const PrefixesNew = () => {
  const { formData, formErrors, updateFormData } = useContext(DataContext);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateFormData(name, value, formErrors[name]);
  };

  const [errorMessage, setErrorMessage] = useState<string[] | undefined>(
    formErrors.prefixes,
  );

  const validateField = () => {
    setErrorMessage(undefined);
    updateFormData('prefixes', formData.prefixes, errorMessage);
  };

  return (
    <BasicInput
      _label={'Tussenvoegsel'}
      _error={errorMessage}
      id="prefixes"
      name="prefixes"
      onClick={() => setErrorMessage(undefined)}
      onChange={handleInputChange}
      onBlur={() => validateField()}
      value={formData.prefixes}
    />
  );
};

const SurnameNew = () => {
  const { formData, formErrors, updateFormData } = useContext(DataContext);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateFormData(name, capitalise(value).trim(), formErrors[name]);
  };

  const [errorMessage, setErrorMessage] = useState<string[] | undefined>(
    formErrors.surname,
  );

  const validateField = (value) => {
    if (isEmpty(value)) {
      setErrorMessage(['Vul hier je achternaam in']);
    } else {
      setErrorMessage(undefined);
    }
    updateFormData('achternaam', formData.surname, errorMessage);
  };

  return (
    <BasicInput
      _label={'Achternaam'}
      _error={errorMessage}
      id="surname"
      name="surname"
      onClick={() => setErrorMessage(undefined)}
      onChange={handleInputChange}
      onBlur={() => validateField(formData.surname.trim())}
      value={formData.surname.trim()}
    />
  );
};

const BirtDateNew = () => {
  const { formData, formErrors, updateFormData } = useContext(DataContext);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const date = normalizeDate()(value);
    const newDate = numericToDate()(date);

    updateFormData(name, newDate.slice(0, 10), formErrors[name]);
  };

  const [errorMessage, setErrorMessage] = useState<string[] | undefined>(
    formErrors.birthDate,
  );

  const validateField = (value) => {
    if (isEmpty(value)) {
      setErrorMessage(['Vul hier je achternaam in']);
    } else {
      const error = isOlderThan(
        {
          invalidDate:
            'Vul hier een geldige geboortedatum in. (vb. 01-01-1970)',
          tooYoung: 'Je kunt een aanvraag doen vanaf 18 jaar',
          tooOld: 'Je kunt een aanvraag doen tot 100 jaar',
        },
        18,
        100,
      )(value) as any;
      setErrorMessage(error || undefined);
    }
    updateFormData('achternaam', formData.birthDate, errorMessage);
  };

  return (
    <DateInput
      _label={'Geboortedatum'}
      _error={errorMessage}
      id="birthDate"
      name="birthDate"
      onClick={() => setErrorMessage(undefined)}
      onChange={handleInputChange}
      onBlur={() => validateField(formData.birthDate.trim())}
      value={formData.birthDate.trim()}
    />
  );
};

const EmailNew = () => {
  const { formData, formErrors, updateFormData } = useContext(DataContext);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateFormData(name, value.trim(), formErrors[name]);
  };

  const [errorMessage, setErrorMessage] = useState<string[] | undefined>(
    formErrors.email,
  );

  const validateField = (value) => {
    if (isEmpty(value)) {
      setErrorMessage(['Vul hier je e-mailadres in']);
    } else if (!isEmailValid(value)) {
      setErrorMessage(['U heeft een niet-bestaand e-mailadres ingevuld.']);
    } else {
      setErrorMessage(undefined);
    }

    updateFormData('email', formData.email, errorMessage);
  };

  return (
    <BasicInput
      _label={'Emailadres'}
      _error={errorMessage}
      id="email"
      name="email"
      onClick={() => setErrorMessage(undefined)}
      onChange={handleInputChange}
      onBlur={() => validateField(formData.email.trim())}
      value={formData.email.trim()}
    />
  );
};
